import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Card, notification } from 'antd';
import { setUser } from '../../redux/actions/authActions';
import FeaturedEvaluationCard from '../FeaturedEvaluationCard/FeaturedEvaluationCard';
import CreateEvaluationCard from '../CreateEvaluationCard/CreateEvaluationCard';
import InfiniteScroll from '../InfiniteScroll/InfiniteScroll';
import Spinner from '../spinner/Spinner';
import { getFeaturedRecruitSessions } from '../../API/services/screeningService';
import { SCREENING_SEARCHES } from '../../constants/routes';
import { t } from '../../i18n/i18n';
import './FeaturedEvaluations.scss';

const FeaturedEvaluations = () => {
  const [featuredEvaluations, setFeaturedEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const user = useSelector((state) => state.auth.user, shallowEqual);
  const currentWorkspace = useSelector((state) => state.auth.currentWorkspace, shallowEqual);
  const dispatch = useDispatch();
  const history = useHistory();
  const isMountedRef = useRef(true);
  const offsetRef = useRef(0);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchFeaturedEvaluations = useCallback(
    async (append = false, workspaceId = null, abortSignal = null) => {
      if (!user?.id) {
        return;
      }

      const currentWorkspaceId = workspaceId || currentWorkspace;
      if (!currentWorkspaceId) {
        return;
      }

      if (!append) {
        offsetRef.current = 0;
        setLoading(true);
      }

      try {
        const filters = {
          partnerId: user.id,
          limit: 10,
          offset: offsetRef.current,
        };
        const res = await getFeaturedRecruitSessions(filters, abortSignal);

        if (isMountedRef.current) {
          setFeaturedEvaluations((prev) => (append ? [...prev, ...res] : res));

          if (res.length === 10) {
            setHasMore(true);
            offsetRef.current += 10;
          } else {
            setHasMore(false);
          }
        }
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          return;
        }
        if (isMountedRef.current && !append) {
          notification.error({
            message:
              t('FEATURED_EVALUATIONS_ERROR_LOADING') || 'Error al cargar evaluaciones destacadas',
            placement: 'topRight',
          });
          setFeaturedEvaluations([]);
        }
      } finally {
        if (isMountedRef.current) {
          if (!append) {
            setLoading(false);
          }
        }
      }
    },
    [user?.id, currentWorkspace]
  );

  useEffect(() => {
    if (!user?.id || !currentWorkspace) {
      return;
    }

    setFeaturedEvaluations([]);
    offsetRef.current = 0;
    setHasMore(true);

    const abortController = new AbortController();
    fetchFeaturedEvaluations(false, currentWorkspace, abortController.signal);

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, currentWorkspace]);

  const loadMoreData = useCallback(() => {
    if (loadingMore || !hasMore || loading) return;
    setLoadingMore(true);
    fetchFeaturedEvaluations(true).finally(() => {
      if (isMountedRef.current) {
        setLoadingMore(false);
      }
    });
  }, [loadingMore, hasMore, loading, fetchFeaturedEvaluations]);

  const handlePinChange = async (action, recruitSessionId, updatedWorkspace) => {
    if (updatedWorkspace) {
      dispatch(
        setUser({
          ...user,
          workspaces: user.workspaces.map((ws) =>
            ws.workspaceId === updatedWorkspace.workspaceId ? { ...ws, ...updatedWorkspace } : ws
          ),
        })
      );
    }

    if (action === 'unpin' || action === 'pin') {
      offsetRef.current = 0;
      setHasMore(true);
      await fetchFeaturedEvaluations(false);
    }
  };

  const currentWorkspaceObj = useMemo(
    () => user?.workspaces?.find((ws) => ws.workspaceId === currentWorkspace),
    [user?.workspaces, currentWorkspace]
  );
  const pinnedCount = currentWorkspaceObj?.pinnedRecruitSessions?.length || 0;

  if (loading || !featuredEvaluations || featuredEvaluations.length === 0) {
    return null;
  }

  const cardTitle = (
    <div className="featured-card-header">
      <span className="featured-title">{t('HOME_ACTIVE_SEARCHES')}</span>
      <button
        className="view-all-button"
        onClick={() => history.push(SCREENING_SEARCHES)}
        data-testid="view-all-evaluations-button"
      >
        {t('HOME_SEARCHES_SEE_ALL_EVALUATIONS')}
      </button>
    </div>
  );

  return (
    <Card className="featured-evaluations-container" title={cardTitle}>
      <div className="featured-grid" ref={scrollContainerRef}>
        <CreateEvaluationCard />
        {featuredEvaluations.map((evaluation) => (
          <FeaturedEvaluationCard
            key={evaluation.recruitSessionId}
            data={evaluation}
            onPinChange={handlePinChange}
            pinnedCount={pinnedCount}
            reloadSearches={async () => {
              offsetRef.current = 0;
              setHasMore(true);
              await fetchFeaturedEvaluations(false);
            }}
          />
        ))}
        {loadingMore && (
          <div className="loading-more-container">
            <Spinner show={loadingMore} />
          </div>
        )}
      </div>
      <InfiniteScroll
        onScrollEnd={loadMoreData}
        isLoading={loadingMore}
        containerRef={scrollContainerRef}
        direction="horizontal"
      />
    </Card>
  );
};

export default FeaturedEvaluations;
